import { useEffect, useState, useRef, Fragment } from "react";
import { useSearchParams } from "react-router-dom";
import { Link, useNavigate } from "react-router-dom";
import { FileText, Plus, Search, AlertTriangle, CheckCircle2, Copy, Files, Trash2, Briefcase, ChevronDown, ChevronRight, Award, Paperclip, Download, GitCompareArrows, Eye, Pencil, ClipboardCheck, ClipboardCopy, Layers, NotepadText } from "lucide-react";
import { copyPart } from "@/utils/partClipboard";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatINR, formatINRCompact } from "@/utils/formatINR";
import QuotePdfDownloader from "@/components/QuotePdfDownloader";

// Module-level restore-window guard (survives React StrictMode's dev double-mount).
let npdRestoreUntil = 0;

const STATUS_BADGE = {
  draft:      "bg-slate-100 text-slate-700",
  n1:         "bg-sky-100 text-sky-700",
  n2:         "bg-sky-100 text-sky-700",
  n3:         "bg-sky-100 text-sky-700",
  n4:         "bg-sky-100 text-sky-700",
  n5:         "bg-sky-100 text-sky-700",
};

// Project lifecycle stage (mirrors ProjectList.jsx).
function projectStage(p) {
  const oc = p.outcome;
  if (oc && ["won", "lost", "dropped", "partially_awarded"].includes(oc)) return "Completed";
  if ((p.current_round || 0) > 0 || (p.status && p.status !== "draft") || (p.parts_with_round_count || 0) > 0) return "Sent";
  return "Draft";
}
const PROJECT_STAGE_BADGE = {
  Draft: "bg-slate-100 text-slate-700",
  Sent: "bg-sky-100 text-sky-700",
  Completed: "bg-emerald-100 text-emerald-700",
};

// Approval pill. Anything not approved for export = "Under Approval" (drafts and rounds alike).
const APPROVAL_BADGE = {
  approved:       { label: "Approved",       cls: "bg-emerald-100 text-emerald-700", title: "Approved for export" },
  under_approval: { label: "Under Approval", cls: "bg-amber-100 text-amber-800",     title: "Not yet approved" },
  draft:          { label: "Draft",          cls: "bg-slate-100 text-slate-600",     title: "No parts yet" },
};
const rowApprovalState = (approved) => (approved ? "approved" : "under_approval");
// Outcome pill — shown prominently (right side) once a costing/project is closed.
const OUTCOME_BADGE = {
  won:               { label: "Won",     cls: "bg-emerald-100 text-emerald-700", title: "Marked Won" },
  lost:              { label: "Lost",    cls: "bg-red-100 text-red-700",         title: "Marked Lost" },
  dropped:           { label: "Dropped", cls: "bg-stone-200 text-stone-700",     title: "Marked Dropped" },
  partially_awarded: { label: "Partial", cls: "bg-purple-100 text-purple-700",   title: "Partially awarded" },
};
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "2-digit" }) : "—");

// Order rows by the running number in AAMPL-U1-NPD-2026-0052 / AAMPL-U1-PRJ-2026-0041[/P2]
// rather than by created_at — the date is back-dated to the enquiry, so it doesn't follow the
// sequence. Compares year first, then the 4-digit serial, then the /P child index.
const seqKey = (num) => {
  const m = String(num || "").match(/-(\d{4})-(\d+)(?:\/P(\d+))?/);
  return m ? [Number(m[1]), Number(m[2]), Number(m[3] || 0)] : [-1, -1, -1];
};
const cmpSeqDesc = (a, b) => {
  const ka = seqKey(a), kb = seqKey(b);
  return (kb[0] - ka[0]) || (kb[1] - ka[1]) || (kb[2] - ka[2]) ||
         String(b || "").localeCompare(String(a || ""));
};

export default function NpdList() {
  const { user, hasPermission } = useAuth();
  const nav = useNavigate();
  // Granular access: full delete vs under-approval-only delete; and viewing approved quotes.
  const canDeleteAny = user?.role === "admin" || hasPermission("npd.delete");
  const canDeleteUnderApproval = hasPermission("under_approval.delete");
  const canViewApproved = user?.role === "admin" || hasPermission("view_approved") || hasPermission("npd.approve");
  // R42 — read filter values from URL on first render so dashboard tile clicks land with the right filters.
  const [searchParams, setSearchParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [assemblies, setAssemblies] = useState([]);   // only fetched/shown in the "All" view
  const [customers, setCustomers] = useState([]);
  const [q, setQ] = useState(searchParams.get("q") || "");
  const [custFilter, setCustFilter] = useState(searchParams.get("customer_id") || "");
  const [custQuery, setCustQuery] = useState("");
  const [custOpen, setCustOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || "");
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [projectFilter, setProjectFilter] = useState(searchParams.get("project_id") || "");
  const [units, setUnits] = useState([]);
  const [unitFilter, setUnitFilter] = useState(searchParams.get("unit_id") || "");
  const [outcomeFilter, setOutcomeFilter] = useState(searchParams.get("outcome") || "");
  const [approvedFilter, setApprovedFilter] = useState(searchParams.get("approved") || "");   // R42
  const [pendingApprovalFilter, setPendingApprovalFilter] = useState(searchParams.get("pending_approval") || "");  // Under-Approval queue
  const loadSeqRef = useRef(0);  // guards against a slow earlier load overwriting a newer one
  const [redFlagFilter, setRedFlagFilter] = useState(searchParams.get("red_flag") || "");     // R42
  const [awardedFilter, setAwardedFilter] = useState(searchParams.get("awarded") || "");     // R42 (potential vs awarded)
  const [showAssemblyChildren, setShowAssemblyChildren] = useState(false);
  const [newMenuOpen, setNewMenuOpen] = useState(false);
  const [pdfNpd, setPdfNpd] = useState(null);  // in-page quote → PDF (no new tab)
  // Category switcher — "All" shows project groups + standalone parts + assemblies,
  // "Projects" shows only the project groups. Assemblies navigates out.
  // The old "Costing" option (groups + standalone, no assemblies) was dropped as redundant;
  // legacy links carrying ?category=Costing fall back to the "All" default.
  const [category, setCategory] = useState(() => {
    const c = searchParams.get("category");
    return !c || c === "Costing" ? "All" : c;
  });
  const groupOnly = category === "Projects" || category === "ProtoProjects";
  const [expandedGroups, setExpandedGroups] = useState({});
  const [lazyChildren, setLazyChildren] = useState({});
  const [loadingChildren, setLoadingChildren] = useState({});
  const newMenuRef = useRef(null);

  // Project clone modal (ported from ProjectList so project-level actions stay intact).
  const [cloneModal, setCloneModal] = useState(null);
  const [cloning, setCloning] = useState(false);
  const [customerListOpen, setCustomerListOpen] = useState(false);
  const customerComboRef = useRef(null);

  useEffect(() => {
    if (!newMenuOpen) return;
    const onDown = (e) => {
      if (newMenuRef.current && !newMenuRef.current.contains(e.target)) {
        setNewMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [newMenuOpen]);

  useEffect(() => {
    if (!customerListOpen) return;
    const onDocClick = (e) => {
      if (customerComboRef.current && !customerComboRef.current.contains(e.target)) {
        setCustomerListOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [customerListOpen]);

  const hideMargin = user?.role === "npd_engineer";

  const cloneRow = async (npd) => {
    if (!window.confirm(`Clone ${npd.npd_number} (${npd.part_name})? A new draft NPD will be created.`)) return;
    try {
      const r = await api.post(`/npd/${npd.npd_id}/clone`);
      toast.success(`Cloned → ${r.data.npd_number}`);
      nav(`/npd/${r.data.npd_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Clone failed");
    }
  };

  const deleteRow = async (npd) => {
    if (!window.confirm(`Soft-delete ${npd.npd_number} (${npd.part_name})?\n\nThe record stays in the database and admins can restore it from the Trash page. If this NPD is the only live costing linked to its RFQ Register entry, that RFQ will also be cascade-deleted.`)) return;
    try {
      await api.delete(`/npd/${npd.npd_id}`);
      toast.success(`Moved ${npd.npd_number} to Trash`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  // Move a project child part OUT of its project so it becomes a standalone single costing.
  // The costing itself is untouched — it just gets a fresh NPD number and leaves the group.
  const convertToSingle = async (npd) => {
    const pid = npd.project_id;
    if (!pid) return;
    if (!window.confirm(
      `Make "${npd.part_name || npd.npd_number}" a single costing?\n\n` +
      `It LEAVES the project ${npd.npd_number?.split("/")[0]} and gets a new standalone NPD number. ` +
      `All costing data, rounds and attachments stay with it. The remaining parts are renumbered P1, P2, …\n\n` +
      `To put it back in a project later, use the copy/paste-part buttons.`
    )) return;
    try {
      const r = await api.post(`/projects/${pid}/parts/${npd.npd_id}/convert-to-single`);
      toast.success(`${r.data.previous_npd_number} → ${r.data.npd_number} (now a single costing)`);
      // The expanded group caches its children — force a refetch so the row disappears from
      // the project and reappears as a standalone row.
      await loadGroupChildren(pid, { force: true });
      loadProjects();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Convert failed");
    }
  };

  // ---- Project-level actions (parity with the retired ProjectList page) ----
  const openCloneModal = (project) => {
    setCloneModal({
      project,
      target_customer_name: customerName(project.customer_id) || "",
      target_project_name: project.project_name || "",
    });
  };

  // One-click "Make a copy" — duplicate a project + all its parts (same customer, name + "(Copy)").
  const copyProject = async (project) => {
    if (!window.confirm(`Make a copy of "${project.project_name}" with all ${project.child_count ?? 0} part(s)?`)) return;
    try {
      const r = await api.post(`/projects/${project.project_id}/clone`, {
        target_customer_name: customerName(project.customer_id) || "Customer",
        target_project_name: `${project.project_name || "Project"} (Copy)`,
      });
      toast.success(`Copied → ${r.data.project_number} (${r.data.children_cloned} parts)`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Copy failed");
    }
  };

  const confirmClone = async () => {
    if (!cloneModal) return;
    const { project, target_customer_name, target_project_name } = cloneModal;
    const trimmed = (target_customer_name || "").trim();
    const trimmedName = (target_project_name || "").trim();
    if (!trimmed) { toast.error("Customer name cannot be empty"); return; }
    if (!trimmedName) { toast.error("Project name cannot be empty"); return; }
    setCloning(true);
    try {
      const r = await api.post(`/projects/${project.project_id}/clone`, {
        target_customer_name: trimmed,
        target_project_name: trimmedName,
      });
      toast.success(`Cloned → ${r.data.project_number} (${r.data.children_cloned} parts)`);
      setCloneModal(null);
      nav(`/projects/${r.data.project_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Clone failed");
    } finally {
      setCloning(false);
    }
  };

  const deleteProject = async (project) => {
    if (!window.confirm(`Soft-delete ${project.project_number} (${project.project_name})?\n\nAll ${project.child_count || 0} child parts will also be soft-deleted (cascade). The linked RFQ Register entry will also be cascade-deleted if no other live costing references it. Admins can restore everything from the Trash page.`)) return;
    try {
      await api.delete(`/projects/${project.project_id}`);
      toast.success(`Moved ${project.project_number} to Trash`);
      loadProjects();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const loadProjects = async () => {
    try {
      // Fetch the full set (endpoint max le=500) so ALL project groups are available for the
      // client-side name search + filters — the default 50 window hid older projects (e.g. a
      // search for an older project name returned nothing because it wasn't loaded).
      const r = await api.get("/projects", { params: { limit: 500 } });
      setProjects(r.data.projects || []);
    } catch { /* ignore */ }
  };

  // Expand/collapse a project group. Child parts are lazy-loaded on first open
  // because the /npd list is paginated (default 50) and older projects' parts
  // fall outside that window.
  const loadGroupChildren = async (pid, { force = false } = {}) => {
    if (!force && (lazyChildren[pid] !== undefined || loadingChildren[pid])) return;
    setLoadingChildren((s) => ({ ...s, [pid]: true }));
    try {
      const r = await api.get("/npd", { params: { project_id: pid, exclude_assembly_children: false, limit: 500 } });
      setLazyChildren((s) => ({ ...s, [pid]: r.data.npds || [] }));
    } catch {
      setLazyChildren((s) => ({ ...s, [pid]: [] }));
    } finally {
      setLoadingChildren((s) => ({ ...s, [pid]: false }));
    }
  };
  const toggleGroup = (p) => {
    const pid = p.project_id;
    const willOpen = !expandedGroups[pid];
    setExpandedGroups((s) => ({ ...s, [pid]: willOpen }));
    if (willOpen) loadGroupChildren(pid);
  };

  const load = async () => {
    // Sequence guard: a slow earlier request (e.g. the initial full-list load) must not
    // overwrite the results of a newer one (e.g. a search) that finished first.
    const seq = ++loadSeqRef.current;
    setLoading(true);
    try {
      const params = {};
      if (q) params.search = q;
      if (custFilter) params.customer_id = custFilter;
      // Status dropdown now filters by approval state (reusing existing backend params).
      // Legacy values (draft / in_negotiation / n1..) from dashboard deep-links still pass through.
      if (statusFilter === "approved") params.approved = "yes";
      else if (statusFilter === "under_approval") params.pending_approval = "yes";
      else if (statusFilter) params.status = statusFilter;
      if (approvedFilter) params.approved = approvedFilter;
      if (pendingApprovalFilter) params.pending_approval = pendingApprovalFilter;
      if (redFlagFilter) params.red_flag = redFlagFilter;
      if (awardedFilter) params.awarded = awardedFilter;
      if (projectFilter === "__standalone__") params.standalone_only = true;
      else if (projectFilter) params.project_id = projectFilter;
      if (unitFilter && user?.role === "admin") params.unit_id = unitFilter;
      if (outcomeFilter) params.outcome = outcomeFilter;
      // When searching by part / NPD #, include assembly component parts too so a part is
      // found wherever it lives (single, project child, or inside an assembly) — ready to copy.
      if (showAssemblyChildren || q) params.exclude_assembly_children = false;
      if (category === "Prototypes") params.is_prototype = "yes";
      // "Single Costing" — only costings that don't belong to a project (incl. parts that
      // were converted out of one). No project group headers render in this view.
      if (category === "Singles") params.standalone_only = true;
      // Fetch the full set (endpoint max le=500) so ALL standalone single costings render
      // as flat rows — the merged list has no pagination and the default 50-row window was
      // shared with project-child parts, burying most singles.
      // STOPGAP: if total costings ever approach 500, replace with real pagination / infinite
      // scroll (backend already supports page/limit).
      params.limit = 500;
      const r = await api.get("/npd", { params });
      if (seq === loadSeqRef.current) setRows(r.data.npds || []);

      // "All" view also folds in assemblies (their own collection). Reuse the shared
      // filters the /assemblies endpoint understands; approval/status is applied client-side
      // below (that endpoint has no approved/pending_approval param). Other categories don't
      // show assemblies, so skip the extra call entirely.
      if (category === "All") {
        const aParams = { limit: 500 };
        if (q) aParams.search = q;
        if (custFilter) aParams.customer_id = custFilter;
        if (outcomeFilter) aParams.outcome = outcomeFilter;
        if (projectFilter === "__standalone__") aParams.standalone_only = true;
        else if (projectFilter) aParams.project_id = projectFilter;
        if (unitFilter && user?.role === "admin") aParams.unit_id = unitFilter;
        try {
          const ar = await api.get("/assemblies", { params: aParams });
          if (seq === loadSeqRef.current) setAssemblies(ar.data.assemblies || []);
        } catch {
          if (seq === loadSeqRef.current) setAssemblies([]);
        }
      } else if (seq === loadSeqRef.current) {
        setAssemblies([]);
      }
    } finally {
      if (seq === loadSeqRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {});
    loadProjects();
    if (user?.role === "admin") {
      api.get("/units").then((r) => setUnits(r.data || [])).catch(() => {});
    }
    // eslint-disable-next-line
  }, [user?.role]);
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    // Refresh BOTH the costings list and the project groups on every filter/view change —
    // otherwise project approval badges stay frozen from the first page load (e.g. a project
    // whose parts got approved still showed "Under Approval" until a full reload).
    const t = setTimeout(() => { load(); loadProjects(); }, 250);
    return () => clearTimeout(t);
    /* eslint-disable-next-line */
  }, [q, custFilter, statusFilter, projectFilter, unitFilter, outcomeFilter, approvedFilter, pendingApprovalFilter, redFlagFilter, awardedFilter, showAssemblyChildren, category]);

  // Under-Approval is reachable from the sidebar while already on /npd (no remount),
  // so sync the filter from the URL when it changes externally. setState bails when
  // the value is unchanged, so this can't loop against the URL-mirror effect below.
  useEffect(() => {
    const v = searchParams.get("pending_approval") || "";
    setPendingApprovalFilter((prev) => (prev === v ? prev : v));
    const a = searchParams.get("approved") || "";
    setApprovedFilter((prev) => (prev === a ? prev : a));
    // eslint-disable-next-line
  }, [searchParams]);

  // Mirror filters into the URL (replace:true so search keystrokes don't spam history).
  useEffect(() => {
    const p = {};
    if (q) p.q = q;
    if (custFilter) p.customer_id = custFilter;
    if (unitFilter) p.unit_id = unitFilter;
    if (statusFilter) p.status = statusFilter;
    if (outcomeFilter) p.outcome = outcomeFilter;
    if (projectFilter) p.project_id = projectFilter;
    if (approvedFilter) p.approved = approvedFilter;
    if (pendingApprovalFilter) p.pending_approval = pendingApprovalFilter;
    if (redFlagFilter) p.red_flag = redFlagFilter;
    if (awardedFilter) p.awarded = awardedFilter;
    if (category && category !== "All") p.category = category;
    setSearchParams(p, { replace: true });
    // eslint-disable-next-line
  }, [q, custFilter, unitFilter, statusFilter, outcomeFilter, projectFilter, approvedFilter, pendingApprovalFilter, redFlagFilter, awardedFilter, category]);

  // Persist expanded groups + scroll position across navigate-away/back (sessionStorage, route-scoped).
  const SS_KEY = "npdlist:state";
  const ssMerge = (patch) => {
    try {
      const prev = JSON.parse(sessionStorage.getItem(SS_KEY) || "{}") || {};
      sessionStorage.setItem(SS_KEY, JSON.stringify({ ...prev, ...patch }));
    } catch { /* ignore */ }
  };
  // On mount: restore expanded groups + scroll. A rAF loop re-applies the scroll each frame
  // (until reached or timeout) so it survives async row/lazy-child rendering. The restore
  // guard (npdRestoreUntil) is module-level so React StrictMode's mount→cleanup→mount cycle
  // can't flip it off mid-restore and let a scroll-to-0 clobber the saved value.
  useEffect(() => {
    let saved = null;
    try { saved = JSON.parse(sessionStorage.getItem(SS_KEY) || "null"); } catch { /* ignore */ }
    if (!saved) return;
    if (saved.expandedGroups && Object.keys(saved.expandedGroups).length) {
      setExpandedGroups(saved.expandedGroups);
      Object.keys(saved.expandedGroups).forEach((pid) => { if (saved.expandedGroups[pid]) loadGroupChildren(pid); });
    }
    const y = saved.scrollY || 0;
    if (!y) return;
    npdRestoreUntil = Date.now() + 1800;
    let raf;
    // Re-apply every frame for the whole window (do NOT early-stop): a debounced reload can
    // briefly swap the table for a "Loading…" div, collapsing height and resetting scroll to 0.
    const tick = () => {
      if (window.scrollY !== y) window.scrollTo(0, y);
      if (Date.now() > npdRestoreUntil) return;
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => { if (raf) cancelAnimationFrame(raf); };
    // eslint-disable-next-line
  }, []);
  // Persist expanded groups whenever they change.
  useEffect(() => { ssMerge({ expandedGroups }); /* eslint-disable-next-line */ }, [expandedGroups]);
  // Persist scroll position as the user scrolls (throttled via rAF). Skip during the restore window,
  // and ignore a sudden jump-to-top (the browser resetting scroll while navigating away, which would
  // otherwise clobber the saved position before unmount).
  useEffect(() => {
    let raf = null;
    let prevY = window.scrollY;
    const onScroll = () => {
      const y = window.scrollY;
      const navReset = y === 0 && prevY > 150;
      prevY = y;
      if (Date.now() < npdRestoreUntil || navReset) return;
      if (raf) return;
      raf = requestAnimationFrame(() => { raf = null; ssMerge({ scrollY: y }); });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => { window.removeEventListener("scroll", onScroll); if (raf) cancelAnimationFrame(raf); };
    // eslint-disable-next-line
  }, []);

  const customerName = (id) => customers.find((c) => c.customer_id === id)?.name || "—";

  // ---- Build the unified (grouped) view from the /npd result set ----
  const projectsById = {};
  projects.forEach((p) => { projectsById[p.project_id] = p; });

  const childrenByProject = {};
  const standaloneRows = [];
  rows.forEach((r) => {
    if (r.project_id) {
      (childrenByProject[r.project_id] = childrenByProject[r.project_id] || []).push(r);
    } else {
      standaloneRows.push(r);
    }
  });

  const projMatchesFilters = (p) => {
    // Under-Approval view: approval lives on individual costings, not projects. Only surface
    // a group when it has matching pending children (added via childrenByProject), never on
    // its own — otherwise empty project headers would flood the queue.
    if (pendingApprovalFilter) return false;
    if (category === "ProtoProjects" && !p.is_prototype) return false;
    if (custFilter && p.customer_id !== custFilter) return false;
    if (unitFilter && user?.role === "admin" && p.unit_id !== unitFilter) return false;
    if (outcomeFilter && (p.outcome || "pending") !== outcomeFilter) return false;
    if (q) {
      const s = q.trim().toLowerCase();
      if (!((p.project_number || "").toLowerCase().includes(s) || (p.project_name || "").toLowerCase().includes(s))) return false;
    }
    return true;
  };

  // Project group ids to render (both views): every project matching the active
  // filters, plus any project that has matching child parts in the current /npd
  // window. Children are lazy-loaded on expand. In the "All" view standalone
  // parts are also shown below; in "Projects" view only the groups are shown.
  const groupIdSet = new Set(projects.filter(projMatchesFilters).map((p) => p.project_id));
  Object.keys(childrenByProject).forEach((id) => groupIdSet.add(id));
  // Prototypes view is a flat list of proto quotes — no project group headers.
  // Project-Proto view shows only prototype project groups.
  const groupIds = (category === "Prototypes" || category === "Singles")
    ? []
    : category === "ProtoProjects"
    ? projects.filter(projMatchesFilters).map((p) => p.project_id)
    : Array.from(groupIdSet);
  // The Approved / Under Approval / Draft status filter is an approval-state filter — apply
  // it to project rows too (they come from /projects, not the approval-filtered /npd query).
  const APPROVAL_FILTERS = ["approved", "under_approval", "draft"];
  const projApprovalState = (p) =>
    p.approval_state || (p.all_children_approved ? "approved" : ((p.child_count ?? 0) > 0 ? "under_approval" : "draft"));
  const displayGroups = groupIds
    .map((id) => projectsById[id])
    .filter(Boolean)
    // Status filter: keep a group when its own rollup matches OR it has matching children in
    // the current (already status-filtered) /npd window — otherwise a partially-approved
    // project would hide its approved/under-approval children when filtering by status+customer.
    .filter((p) => !APPROVAL_FILTERS.includes(statusFilter)
      || projApprovalState(p) === statusFilter
      || (childrenByProject[p.project_id] && childrenByProject[p.project_id].length > 0))
    // Approved-quotes visibility gate (view_approved permission).
    .filter((p) => canViewApproved || projApprovalState(p) !== "approved")
    // Highest project number first — the created date can't be trusted for ordering
    // (it is back-dated to the enquiry), so the sequence is what the user reads.
    .sort((a, b) => cmpSeqDesc(a.project_number, b.project_number));

  // Proto Costing is a flat list of EVERY proto quote (backend already filtered is_prototype).
  // Those belonging to a proto project carry a project_id, so they'd otherwise be bucketed
  // under childrenByProject and hidden (no group headers render in this view). Show all rows.
  const flatRows = (category === "Prototypes" ? rows : standaloneRows)
    .filter((n) => canViewApproved || !n.approved_for_export)   // approved-quotes visibility gate
    // Sequence-number order (…-NPD-2026-0052 → 0051 → 0050 …), newest number on top.
    .sort((a, b) => cmpSeqDesc(a.npd_number, b.npd_number));

  // Assemblies — only shown in the "All" view (folded in next to costings & projects).
  // The /assemblies endpoint can't filter by approval state, so the Status dropdown and the
  // sidebar Approved / Under-Approval filters are applied here, client-side.
  const asmApprovalState = (a) => {
    if (a.approved_for_export) return "approved";
    const st = String(a.status || "").toLowerCase();
    return st && st !== "draft" ? "under_approval" : "draft";
  };
  const displayAssemblies = (category === "All" ? assemblies : [])
    .filter((a) => canViewApproved || !a.approved_for_export)            // approved-quotes visibility gate
    .filter((a) => !APPROVAL_FILTERS.includes(statusFilter) || asmApprovalState(a) === statusFilter)
    .filter((a) => approvedFilter !== "yes" || a.approved_for_export)     // sidebar "Approved Quotes"
    .filter((a) => pendingApprovalFilter !== "yes" || asmApprovalState(a) === "under_approval");  // sidebar "Under Approval"

  const totalCols = 9 + (user?.role === "admin" ? 1 : 0) + (hideMargin ? 0 : 1);   // +1 for the Date column
  const hasContent = displayGroups.length > 0 || (!groupOnly && flatRows.length > 0) || displayAssemblies.length > 0;

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto" data-testid="npd-list-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            {pendingApprovalFilter
              ? <><ClipboardCheck size={22} className="text-amber-600" /> Under Approval</>
              : approvedFilter
              ? <><CheckCircle2 size={22} className="text-emerald-600" /> Approved Quotes</>
              : <><FileText size={22} className="text-orange-600" /> NPD Costings</>}
          </h1>
          <button
            type="button"
            onClick={async () => {
              const params = new URLSearchParams();
              if (q) params.set("search", q);
              if (custFilter) params.set("customer_id", custFilter);
              if (statusFilter) params.set("status", statusFilter);
              if (projectFilter) params.set("project_id", projectFilter);
              if (unitFilter && user?.role === "admin") params.set("unit_id", unitFilter);
              if (outcomeFilter) params.set("outcome", outcomeFilter);
              if (pendingApprovalFilter) params.set("pending_approval", pendingApprovalFilter);
              if (showAssemblyChildren) params.set("exclude_assembly_children", "false");
              const res = await api.get(`/npd/_/export.xlsx?${params.toString()}`, { responseType: "blob" });
              const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
              const link = document.createElement("a");
              link.href = URL.createObjectURL(blob);
              link.download = (res.headers?.["content-disposition"] || "").match(/filename="([^"]+)"/)?.[1] || "AAMPL_NPDs.xlsx";
              link.click();
              URL.revokeObjectURL(link.href);
            }}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-slate-300 rounded-md text-slate-700 hover:bg-slate-50 mt-2"
            data-testid="npd-export-btn"
          >
            <Download size={14} /> Export Excel
          </button>
          <p className="text-sm text-slate-500 mt-0.5">
            {hideMargin ? "Costings assigned to you." : "All costings and projects together — single, bulk and prototype parts."}
          </p>
        </div>

        {/* Branched New button */}
        <div className="relative" ref={newMenuRef}>
          <Link to="/npd/compare" className="inline-flex items-center gap-2 border border-slate-300 text-slate-700 hover:bg-slate-50 text-sm font-medium rounded-md px-3 py-2 mr-2" data-testid="npd-compare-cross">
            <GitCompareArrows size={14} /> Compare Two
          </Link>
          <button
            onClick={() => setNewMenuOpen((o) => !o)}
            className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white text-sm font-semibold rounded-md px-4 py-2"
            data-testid="npd-new"
          >
            <Plus size={16} /> New Costing <ChevronDown size={14} className={`transition ${newMenuOpen ? "rotate-180" : ""}`} />
          </button>
          {newMenuOpen && (
            <div className="absolute right-0 mt-1 w-64 bg-white border border-slate-200 rounded-md shadow-lg z-20 py-1" data-testid="npd-new-menu">
              <button
                onClick={() => { setNewMenuOpen(false); nav("/npd/new"); }}
                className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-start gap-2"
                data-testid="npd-new-single"
              >
                <FileText size={16} className="mt-0.5 text-orange-600" />
                <div>
                  <div className="text-sm font-medium text-slate-900">Single Costing</div>
                  <div className="text-xs text-slate-500">One-off RFQ for a single part</div>
                </div>
              </button>
              <button
                onClick={() => { setNewMenuOpen(false); nav("/npd/new?proto=1"); }}
                className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-start gap-2 border-t border-slate-100"
                data-testid="proto-new-single"
              >
                <FileText size={16} className="mt-0.5 text-orange-600" />
                <div>
                  <div className="text-sm font-medium text-slate-900">Prototype Costing</div>
                  <div className="text-xs text-slate-500">One-off prototype RFQ for a single part</div>
                </div>
              </button>
              <button
                onClick={() => { setNewMenuOpen(false); nav("/projects/new"); }}
                className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-start gap-2 border-t border-slate-100"
                data-testid="npd-new-project-series"
              >
                <Briefcase size={16} className="mt-0.5 text-orange-600" />
                <div>
                  <div className="text-sm font-medium text-slate-900">Project (Series)</div>
                  <div className="text-xs text-slate-500">Bulk RFQ — many parts, full production costing</div>
                </div>
              </button>
              <button
                onClick={() => { setNewMenuOpen(false); nav("/projects/new?proto=1"); }}
                className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-start gap-2 border-t border-slate-100"
                data-testid="npd-new-project-proto"
              >
                <Briefcase size={16} className="mt-0.5 text-orange-600" />
                <div>
                  <div className="text-sm font-medium text-slate-900">Proto Project</div>
                  <div className="text-xs text-slate-500">Bulk prototype RFQ — reduced sections</div>
                </div>
              </button>
            </div>
          )}
        </div>
      </div>

      {pendingApprovalFilter && (
        <div className="mb-4 flex items-center justify-between gap-3 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3" data-testid="pending-approval-banner">
          <div className="flex items-center gap-2 text-sm text-amber-800">
            <ClipboardCheck size={18} className="text-amber-600 flex-shrink-0" />
            <span>
              Showing every quote not yet approved (drafts and any negotiation round). Approving a quote removes it here; editing an approved quote or saving a new round brings it back.
            </span>
          </div>
          <Link
            to="/npd"
            onClick={() => setPendingApprovalFilter("")}
            className="text-sm font-medium text-amber-800 hover:text-amber-900 underline whitespace-nowrap"
            data-testid="pending-approval-clear"
          >
            View all
          </Link>
        </div>
      )}

      {approvedFilter && !pendingApprovalFilter && (
        <div className="mb-4 flex items-center justify-between gap-3 bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3" data-testid="approved-quotes-banner">
          <div className="flex items-center gap-2 text-sm text-emerald-800">
            <CheckCircle2 size={18} className="text-emerald-600 flex-shrink-0" />
            <span>Showing quotes approved for export. Editing an approved quote or saving a new round moves it back to Under Approval.</span>
          </div>
          <Link
            to="/npd"
            onClick={() => setApprovedFilter("")}
            className="text-sm font-medium text-emerald-800 hover:text-emerald-900 underline whitespace-nowrap"
            data-testid="approved-quotes-clear"
          >
            View all
          </Link>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-lg">
        <div className={`p-4 border-b border-slate-200 grid grid-cols-1 ${user?.role === "admin" ? "md:grid-cols-6" : "md:grid-cols-5"} gap-3`}>
          <div className="relative">
            <Search size={16} className="absolute left-3 top-2.5 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search any part no. / name / NPD # — incl. project & assembly parts"
                   title="Finds the part anywhere — single costing, project child, or assembly component — so you can copy it from here"
                   className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" data-testid="npd-search" />
          </div>
          {/* Searchable customer picker (reuses the ProjectList combobox pattern).
              Sets the same custFilter (customer_id) so list filtering is unchanged. */}
          <div className="relative" data-testid="npd-customer-filter">
            <input
              type="text"
              value={custOpen ? custQuery : (customers.find((c) => c.customer_id === custFilter)?.name || "")}
              onChange={(e) => { setCustQuery(e.target.value); setCustOpen(true); }}
              onFocus={() => { setCustQuery(""); setCustOpen(true); }}
              onBlur={() => setTimeout(() => setCustOpen(false), 150)}
              placeholder="All customers"
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white"
              data-testid="npd-customer-filter-input"
            />
            {custOpen && (
              <ul className="absolute z-30 mt-1 w-full max-h-64 overflow-y-auto bg-white border border-slate-200 rounded-md shadow-lg text-sm" data-testid="npd-customer-filter-list">
                <li
                  onMouseDown={(e) => { e.preventDefault(); setCustFilter(""); setCustOpen(false); }}
                  className="px-3 py-1.5 hover:bg-orange-50 cursor-pointer text-slate-500 italic border-b border-slate-100"
                  data-testid="npd-customer-filter-all"
                >
                  All customers
                </li>
                {(() => {
                  const query = custQuery.trim().toLowerCase();
                  const list = query
                    ? customers.filter((c) => (c.name || "").toLowerCase().includes(query))
                    : customers;
                  if (list.length === 0) return <li className="px-3 py-2 text-slate-400 italic">No matches</li>;
                  return list.map((c) => (
                    <li
                      key={c.customer_id}
                      onMouseDown={(e) => { e.preventDefault(); setCustFilter(c.customer_id); setCustOpen(false); }}
                      className="px-3 py-1.5 hover:bg-orange-50 cursor-pointer text-slate-800"
                      data-testid="npd-customer-filter-option"
                    >
                      {c.name}
                    </li>
                  ));
                })()}
              </ul>
            )}
          </div>
          {user?.role === "admin" && (
            <select value={unitFilter} onChange={(e) => setUnitFilter(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white"
                    data-testid="npd-unit-filter">
              <option value="">All units</option>
              {units.map((u) => <option key={u.unit_id} value={u.unit_id}>{u.unit_code} — {u.unit_name}</option>)}
            </select>
          )}
          {/* Category switcher — filters the unified list in place.
              "Projects" shows only project group rows; "Assemblies" navigates out. */}
          <select
            value={category}
            onChange={(e) => {
              const v = e.target.value;
              if (v === "Assemblies") { nav("/assemblies"); return; }
              setCategory(v);
            }}
            className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white"
            data-testid="npd-category-switcher"
          >
            <option value="All">All</option>
            <option value="Singles">Single Costing</option>
            <option value="Projects">Projects</option>
            <option value="Prototypes">Proto Costing</option>
            <option value="ProtoProjects">Proto Project</option>
            <option value="Assemblies">Assemblies</option>
          </select>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white" data-testid="npd-status-filter">
            <option value="">All statuses</option>
            {canViewApproved && <option value="approved">Approved</option>}
            <option value="under_approval">Under Approval</option>
            <option value="draft">Draft</option>
          </select>
          <select
            value={outcomeFilter}
            onChange={(e) => setOutcomeFilter(e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white"
            data-testid="npd-outcome-filter"
          >
            <option value="">All outcomes</option>
            <option value="pending">Pending</option>
            <option value="won">Won</option>
            <option value="lost">Lost</option>
            <option value="dropped">Dropped</option>
          </select>
        </div>
        {(approvedFilter || redFlagFilter || awardedFilter || statusFilter === "in_negotiation") && (
          <div className="px-4 pt-2 pb-1 flex items-center gap-2 flex-wrap" data-testid="dashboard-filter-banner">
            <span className="text-xs text-slate-500">From dashboard:</span>
            {statusFilter === "in_negotiation" && (
              <span className="inline-flex items-center gap-1 text-xs bg-sky-50 text-sky-700 border border-sky-200 rounded-full px-2 py-0.5">In Negotiation (n1–n5)</span>
            )}
            {approvedFilter === "yes" && (
              <span className="inline-flex items-center gap-1 text-xs bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full px-2 py-0.5">Approved for Export</span>
            )}
            {redFlagFilter === "yes" && (
              <span className="inline-flex items-center gap-1 text-xs bg-red-50 text-red-700 border border-red-200 rounded-full px-2 py-0.5">Margin Red Flag</span>
            )}
            {awardedFilter === "yes" && (
              <span className="inline-flex items-center gap-1 text-xs bg-amber-50 text-amber-700 border border-amber-200 rounded-full px-2 py-0.5">Awarded Business</span>
            )}
            {awardedFilter === "no" && (
              <span className="inline-flex items-center gap-1 text-xs bg-sky-50 text-sky-700 border border-sky-200 rounded-full px-2 py-0.5">Potential Business (not yet awarded)</span>
            )}
            <button
              type="button"
              onClick={() => {
                setStatusFilter(""); setApprovedFilter(""); setRedFlagFilter(""); setAwardedFilter("");
                setSearchParams({});
              }}
              className="text-xs text-slate-500 hover:text-slate-700 underline ml-1"
              data-testid="clear-dashboard-filter"
            >
              clear
            </button>
          </div>
        )}
        <div className="px-4 pb-3 -mt-1">
          <label className="inline-flex items-center gap-2 text-xs text-slate-600 cursor-pointer" data-testid="npd-show-asm-children-toggle">
            <input
              type="checkbox"
              checked={showAssemblyChildren}
              onChange={(e) => setShowAssemblyChildren(e.target.checked)}
              className="w-3.5 h-3.5 accent-orange-600"
            />
            Show assembly child parts (hidden by default to prevent double-counting — their value is already inside the assembly total)
          </label>
        </div>

        {loading ? (
          <div className="p-10 text-center text-sm text-slate-400">Loading…</div>
        ) : !hasContent ? (
          <EmptyState groupOnly={groupOnly} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="npd-table">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-2 py-2.5 font-semibold">NPD #</th>
                  {user?.role === "admin" && <th className="text-left px-2 py-2.5 font-semibold">Unit</th>}
                  <th className="text-left px-2 py-2.5 font-semibold whitespace-nowrap">Date</th>
                  <th className="text-left px-2 py-2.5 font-semibold">Customer</th>
                  <th className="text-left px-2 py-2.5 font-semibold whitespace-nowrap">Project / Model</th>
                  <th className="text-left px-2 py-2.5 font-semibold whitespace-nowrap">No. of Parts</th>
                  <th className="text-left px-2 py-2.5 font-semibold">Status</th>
                  <th className="text-right px-2 py-2.5 font-semibold">Grand Total</th>
                  <th className="text-right px-2 py-2.5 font-semibold">Dev Cost</th>
                  {!hideMargin && <th className="text-right px-2 py-2.5 font-semibold">Margin %</th>}
                  <th className="text-right px-2 py-2.5 font-semibold">Quote</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {displayGroups.map((p) => {
                  // Parts inside a project read in part order: P1, P2, P3 …
                  const kids = [...(lazyChildren[p.project_id] || childrenByProject[p.project_id] || [])]
                    .sort((a, b) => (a.child_index ?? 0) - (b.child_index ?? 0));
                  const open = !!expandedGroups[p.project_id];
                  const isLoadingKids = !!loadingChildren[p.project_id];
                  const st = projectStage(p);
                  return (
                    <Fragment key={p.project_id}>
                      <tr className="bg-slate-50/70 hover:bg-slate-100" data-testid={`project-group-${p.project_number}`}>
                        {/* NPD # — project number with the expand toggle */}
                        <td className="px-2 py-2.5">
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => toggleGroup(p)}
                              className="text-slate-500 hover:text-slate-800 transition"
                              title={open ? "Collapse" : "Expand"}
                              data-testid={`project-group-toggle-${p.project_number}`}
                            >
                              {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                            </button>
                            <Briefcase size={15} className="text-orange-600 flex-shrink-0" />
                            <span className="font-mono text-xs text-slate-600">{p.project_number}</span>
                          </div>
                        </td>
                        {user?.role === "admin" && (
                          <td className="px-2 py-2.5">
                            <span className="text-[10px] font-mono bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded">
                              {(units.find((un) => un.unit_id === p.unit_id) || {}).unit_code || "—"}
                            </span>
                          </td>
                        )}
                        <td className="px-2 py-2.5 text-xs text-slate-500 whitespace-nowrap" title="Created / received">{fmtDate(p.created_at)}</td>
                        {/* Customer */}
                        <td className="px-2 py-2.5 text-slate-700">{customerName(p.customer_id)}</td>
                        {/* Model — project name + Proto Project badge */}
                        <td className="px-2 py-2.5">
                          <div className="flex items-center gap-1.5">
                            <span className="font-medium text-slate-900 truncate max-w-[160px]">{p.project_name}</span>
                            {p.is_prototype && (
                              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 uppercase tracking-wide whitespace-nowrap" data-testid={`proj-proto-badge-${p.project_number}`}>Proto PRJ</span>
                            )}
                          </div>
                        </td>
                        {/* No. of Parts */}
                        <td className="px-2 py-2.5 text-xs text-slate-600 whitespace-nowrap">{(p.child_count ?? kids.length) || 1} part{((p.child_count ?? kids.length) || 1) === 1 ? "" : "s"}</td>
                        {/* Status — stage + outcome */}
                        <td className="px-2 py-2.5">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={`text-xs font-medium px-2 py-0.5 rounded ${PROJECT_STAGE_BADGE[st]}`}>{st}</span>
                            {p.outcome === "won" && (
                              <span className="inline-flex items-center gap-1 text-amber-700 text-[10px]"><Award size={11} /> Won</span>
                            )}
                            {p.outcome === "lost" && <span className="text-red-700 text-[10px]">Lost</span>}
                            {p.outcome === "dropped" && <span className="text-stone-700 text-[10px]">Dropped</span>}
                            {p.outcome === "partially_awarded" && <span className="text-purple-700 text-[10px]">Partial</span>}
                          </div>
                        </td>
                        {/* Grand Total — per-piece N/A for a group; show the project's TOTAL value instead */}
                        <td className="px-2 py-2.5 text-right font-mono text-slate-800 font-semibold" title={p.is_prototype ? "Total Prototype Value (qty × price + dev)" : "Total Business Value across all parts"} data-testid={`project-total-value-${p.project_number}`}>
                          <span title={p.project_total_value != null ? formatINR(p.project_total_value) : ""}>{p.project_total_value != null ? formatINRCompact(p.project_total_value) : "—"}</span>
                        </td>
                        <td className="px-2 py-2.5 text-right font-mono text-slate-700 font-semibold" data-testid={`project-devcost-${p.project_number}`}>
                          <span title={formatINR(p.children_development_cost_total)}>{formatINRCompact(p.children_development_cost_total)}</span>
                        </td>
                        {!hideMargin && <td className="px-2 py-2.5 text-right text-slate-300">—</td>}
                        {/* Quote — approval rollup on top, actions (copy / delete / open) below */}
                        <td className="px-2 py-2.5 align-top">
                          <div className="flex flex-col items-end gap-1.5">
                            {/* Closed project → prominent outcome pill; otherwise the approval
                                rollup — Approved (all parts approved) / Under Approval / Draft. */}
                            {OUTCOME_BADGE[p.outcome] ? (
                              <span className={`text-[10px] px-1.5 py-0.5 rounded whitespace-nowrap ${OUTCOME_BADGE[p.outcome].cls}`} title={OUTCOME_BADGE[p.outcome].title} data-testid={`project-outcome-badge-${p.project_number}`}>{OUTCOME_BADGE[p.outcome].label}</span>
                            ) : (p.child_count ?? 0) > 0 && (() => {
                              const st = p.approval_state || (p.all_children_approved ? "approved" : "draft");
                              const b = APPROVAL_BADGE[st] || APPROVAL_BADGE.draft;
                              return <span className={`text-[10px] px-1.5 py-0.5 rounded whitespace-nowrap ${b.cls}`} title={b.title} data-testid={`project-${st}-${p.project_number}`}>{b.label}</span>;
                            })()}
                            <div className="inline-flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => copyProject(p)}
                                className="text-slate-400 hover:text-emerald-600 transition"
                                title="Make a copy (one click — same customer, all parts)"
                                data-testid={`project-copy-${p.project_number}`}
                              >
                                <Files size={15} />
                              </button>
                              <button
                                type="button"
                                onClick={() => openCloneModal(p)}
                                className="text-slate-400 hover:text-orange-600 transition"
                                title="Clone to another customer / rename (choose target)"
                                data-testid={`project-clone-${p.project_number}`}
                              >
                                <Copy size={15} />
                              </button>
                              {user?.role === "admin" && (
                                <button
                                  type="button"
                                  onClick={() => deleteProject(p)}
                                  className="text-slate-400 hover:text-red-600 transition"
                                  title="Soft-delete project + all parts (admin only)"
                                  data-testid={`project-delete-${p.project_number}`}
                                >
                                  <Trash2 size={15} />
                                </button>
                              )}
                              <Link
                                to={`/projects/${p.project_id}`}
                                className="text-orange-600 hover:text-orange-700 text-sm font-medium whitespace-nowrap"
                                data-testid={`project-open-${p.project_number}`}
                              >
                                Open →
                              </Link>
                            </div>
                          </div>
                        </td>
                      </tr>
                      {open && kids.map((n) => (
                        <CostingRow
                          key={n.npd_id}
                          n={n}
                          user={user}
                          units={units}
                          hideMargin={hideMargin}
                          customerName={customerName}
                          cloneRow={cloneRow}
                          deleteRow={deleteRow}
                          convertToSingle={convertToSingle}
                          onDownloadPdf={setPdfNpd}
                          pdfBusyId={pdfNpd?.npd_id}
                          canDeleteAny={canDeleteAny}
                          canDeleteUnderApproval={canDeleteUnderApproval}
                          indented
                        />
                      ))}
                      {open && kids.length === 0 && (
                        <tr>
                          <td colSpan={totalCols} className="px-3 py-2 pl-12 text-xs text-slate-400 italic">
                            {isLoadingKids ? "Loading parts…" : "No parts match the current filters."}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
                {!groupOnly && flatRows.map((n) => (
                  <CostingRow
                    key={n.npd_id}
                    n={n}
                    user={user}
                    units={units}
                    hideMargin={hideMargin}
                    customerName={customerName}
                    cloneRow={cloneRow}
                    deleteRow={deleteRow}
                    convertToSingle={convertToSingle}
                    onDownloadPdf={setPdfNpd}
                    pdfBusyId={pdfNpd?.npd_id}
                    canDeleteAny={canDeleteAny}
                    canDeleteUnderApproval={canDeleteUnderApproval}
                  />
                ))}
                {displayAssemblies.map((a) => (
                  <AssemblyRow
                    key={a.assembly_id}
                    a={a}
                    user={user}
                    units={units}
                    hideMargin={hideMargin}
                    customerName={customerName}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Project clone modal (ported from ProjectList) */}
      {cloneModal && (
        <div className="fixed inset-0 bg-slate-900/50 z-40 flex items-center justify-center p-4" data-testid="clone-modal-backdrop">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-5" data-testid="clone-modal">
            <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <Copy size={18} className="text-orange-600" /> Clone project
            </h3>
            <p className="text-xs text-slate-500 mt-1">
              Source: <span className="font-mono">{cloneModal.project.project_number}</span> · {cloneModal.project.child_count || 0} parts
              {cloneModal.project.assemblies_count ? ` · ${cloneModal.project.assemblies_count} assemblies` : ""}
            </p>

            <div className="mt-4">
              <label className="block text-xs font-medium text-slate-600 mb-1">Project name for the clone</label>
              <input
                type="text"
                value={cloneModal.target_project_name}
                onChange={(e) => setCloneModal({ ...cloneModal, target_project_name: e.target.value })}
                placeholder="Name for the new project…"
                className="w-full px-2 py-2 border border-slate-300 rounded text-sm bg-white"
                data-testid="clone-project-name"
                disabled={cloning}
              />
              {!(cloneModal.target_project_name || "").trim() && (
                <div className="text-[11px] text-red-600 mt-1">⚠ Project name is required.</div>
              )}
            </div>

            <div className="mt-4">
              <label className="block text-xs font-medium text-slate-600 mb-1">Customer name for the clone</label>
              <div ref={customerComboRef} className="relative">
                <div className="flex items-stretch">
                  <input
                    type="text"
                    value={cloneModal.target_customer_name}
                    onChange={(e) => setCloneModal({ ...cloneModal, target_customer_name: e.target.value })}
                    placeholder="Pick from list or type a new customer name…"
                    className="w-full px-2 py-2 border border-slate-300 rounded-l rounded-r-none text-sm bg-white border-r-0"
                    data-testid="clone-customer-name"
                    disabled={cloning}
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => setCustomerListOpen((v) => !v)}
                    className="px-2 border border-slate-300 rounded-r bg-slate-50 hover:bg-slate-100 disabled:opacity-50"
                    disabled={cloning}
                    aria-label="Toggle customer list"
                    data-testid="clone-customer-dropdown-toggle"
                  >
                    <ChevronDown size={16} className={`text-slate-600 transition ${customerListOpen ? "rotate-180" : ""}`} />
                  </button>
                </div>
                {customerListOpen && (
                  <ul
                    className="absolute z-50 mt-1 w-full max-h-64 overflow-y-auto bg-white border border-slate-200 rounded-md shadow-lg text-sm"
                    data-testid="clone-customer-list"
                  >
                    {customers.length === 0 && (
                      <li className="px-3 py-2 text-slate-400 italic">No customers in master.</li>
                    )}
                    {customers.map((c) => (
                      <li
                        key={c.customer_id}
                        onClick={() => {
                          setCloneModal({ ...cloneModal, target_customer_name: c.name });
                          setCustomerListOpen(false);
                        }}
                        className="px-3 py-1.5 hover:bg-orange-50 cursor-pointer text-slate-800"
                      >
                        {c.name}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              {(() => {
                const typed = (cloneModal.target_customer_name || "").trim();
                const sourceName = customerName(cloneModal.project.customer_id) || "";
                if (!typed) {
                  return <div className="text-[11px] text-red-600 mt-1">⚠ Customer name is required.</div>;
                }
                if (typed.toLowerCase() === sourceName.toLowerCase()) {
                  return <div className="text-[11px] text-slate-500 mt-1">Same as source customer — no change.</div>;
                }
                const existsInMaster = customers.some(
                  (c) => (c.name || "").trim().toLowerCase() === typed.toLowerCase()
                );
                if (existsInMaster) {
                  return (
                    <div className="text-[11px] text-amber-700 mt-1">
                      ⚠ Customer will change from "{sourceName}" to "{typed}" (already in master) on the cloned project + all parts.
                    </div>
                  );
                }
                return (
                  <div className="text-[11px] text-amber-700 mt-1">
                    ⚠ "{typed}" is NOT in the customer master — a new customer record will be auto-created and the clone + all parts will use it.
                  </div>
                );
              })()}
            </div>

            <div className="flex justify-end gap-2 mt-5">
              <button
                onClick={() => setCloneModal(null)}
                className="px-3 py-1.5 text-sm border border-slate-300 rounded text-slate-700 hover:bg-slate-50"
                disabled={cloning}
                data-testid="clone-cancel"
              >
                Cancel
              </button>
              <button
                onClick={confirmClone}
                className="px-4 py-1.5 text-sm bg-orange-600 hover:bg-orange-700 text-white rounded font-semibold disabled:bg-orange-300"
                disabled={cloning || !(cloneModal.target_customer_name || "").trim() || !(cloneModal.target_project_name || "").trim()}
                data-testid="clone-confirm"
              >
                {cloning ? "Cloning…" : "Clone"}
              </button>
            </div>
          </div>
        </div>
      )}

      <QuotePdfDownloader npd={pdfNpd} onDone={() => setPdfNpd(null)} />
    </div>
  );
}

function CostingRow({ n, user, units, hideMargin, customerName, cloneRow, deleteRow, convertToSingle, onDownloadPdf, pdfBusyId, indented, canDeleteAny, canDeleteUnderApproval }) {
  const canDeleteThis = canDeleteAny || (canDeleteUnderApproval && !n.approved_for_export);
  return (
    <tr className={`hover:bg-slate-50 ${n.is_prototype ? "bg-purple-50/50" : ""}`} data-testid={`npd-row-${n.npd_number}`}>
      <td className={`px-2 py-2.5 font-mono text-xs text-slate-600 ${indented ? "pl-8" : ""}`}>
        <div>{n.npd_number}</div>
        {n.approved_for_export && (
          <div className="flex items-center gap-1 text-emerald-600 text-[10px] mt-0.5">
            <CheckCircle2 size={11} /> Approved
          </div>
        )}
        {n.outcome === "won" && (
          <div className="flex items-center gap-1 text-amber-700 text-[10px] mt-0.5" data-testid={`npd-outcome-won-${n.npd_number}`}>
            <Award size={11} /> Won
          </div>
        )}
        {n.outcome === "lost" && (
          <div className="text-red-700 text-[10px] mt-0.5" data-testid={`npd-outcome-lost-${n.npd_number}`}>Lost</div>
        )}
        {n.outcome === "dropped" && (
          <div className="text-stone-700 text-[10px] mt-0.5" data-testid={`npd-outcome-dropped-${n.npd_number}`}>Dropped</div>
        )}
        {(n.misc_files || []).length > 0 && (
          <div className="flex items-center gap-1 text-slate-500 text-[10px] mt-0.5" title={`${n.misc_files.length} attachment${n.misc_files.length > 1 ? "s" : ""}`} data-testid={`npd-attachments-${n.npd_number}`}>
            <Paperclip size={11} /> {n.misc_files.length}
          </div>
        )}
      </td>
      {user?.role === "admin" && (
        <td className="px-2 py-2.5">
          <span className="text-xs font-mono bg-slate-100 text-slate-700 px-2 py-0.5 rounded" data-testid={`npd-unit-${n.npd_number}`}>
            {(units.find((un) => un.unit_id === n.unit_id) || {}).unit_code || "—"}
          </span>
        </td>
      )}
      <td className="px-2 py-2.5 text-xs text-slate-500 whitespace-nowrap" title={n.approved_for_export && n.approved_at ? `Approved ${fmtDate(n.approved_at)}` : "Created / received"}>{fmtDate(n.created_at)}</td>
      <td className="px-2 py-2.5 text-slate-700">{customerName(n.customer_id)}</td>
      <td className="px-2 py-2.5">
        <div className="font-medium text-slate-900 truncate max-w-[180px] flex items-center gap-1">
          {n.approved_for_export && (
            <CheckCircle2 size={14} className="text-emerald-600 shrink-0" title={`Approved${n.approved_at ? ` · ${fmtDate(n.approved_at)}` : ""}`} data-testid={`npd-approved-tick-${n.npd_number}`} />
          )}
          <span className="truncate">{n.part_name}</span>
          {n.is_prototype && (
            <span className="ml-1 text-[9px] font-bold px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 uppercase tracking-wide align-middle whitespace-nowrap shrink-0" data-testid={`npd-proto-badge-${n.npd_number}`}>Proto Cost</span>
          )}
        </div>
        <div className="text-xs text-slate-500">{n.part_number}</div>
      </td>
      <td className="px-2 py-2.5">
        {n.project_id ? (
          <Link
            to={`/projects/${n.project_id}`}
            className="inline-flex items-center gap-1 text-xs font-mono bg-sky-50 text-sky-700 hover:bg-sky-100 px-2 py-0.5 rounded border border-sky-200 transition"
            title="View this project"
            data-testid={`npd-project-badge-${n.npd_number}`}
          >
            <Briefcase size={11} />
            {n.npd_number?.split("/")[0]}
            {n.child_index ? ` · P${n.child_index}` : ""}
          </Link>
        ) : (
          <span className="text-[11px] text-slate-500">1 part</span>
        )}
      </td>
      <td className="px-2 py-2.5">
        <span className={`text-xs font-medium px-2 py-0.5 rounded ${STATUS_BADGE[n.status] || STATUS_BADGE.draft}`}>
          {(n.status || "draft").toUpperCase()}
        </span>
        {n.current_round > 0 && <span className="ml-2 text-xs text-slate-400">R{n.current_round}</span>}
      </td>
      <td className="px-2 py-2.5 text-right font-mono"><span title={formatINR(n.grand_total)}>{formatINRCompact(n.grand_total)}</span></td>
      <td className="px-2 py-2.5 text-right font-mono text-slate-600" data-testid={`npd-devcost-${n.npd_number}`}><span title={formatINR(n.total_development_cost)}>{formatINRCompact(n.total_development_cost)}</span></td>
      {!hideMargin && (
        <td className="p-3 text-right">
          {n.margin_pct === null || n.margin_pct === undefined ? (
            <span className="text-slate-400">—</span>
          ) : (
            <span className={`font-mono ${n.margin_red_flag ? "text-red-600 font-semibold" : "text-slate-700"}`}>
              {n.margin_pct?.toFixed(2)}%
              {n.margin_red_flag && <AlertTriangle size={12} className="inline ml-1" />}
            </span>
          )}
        </td>
      )}
      <td className="px-2 py-2.5 text-right align-top">
        <div className="flex flex-col items-end gap-1.5">
          {(() => {
            // A closed costing (Won / Lost / Dropped / Partial) shows its outcome pill
            // prominently here instead of an approval nag.
            const ob = OUTCOME_BADGE[n.outcome];
            if (ob) {
              return <span className={`text-[10px] px-1.5 py-0.5 rounded whitespace-nowrap ${ob.cls}`} title={ob.title} data-testid={`npd-outcome-badge-${n.npd_number}`}>{ob.label}</span>;
            }
            const st = rowApprovalState(n.approved_for_export);
            const b = APPROVAL_BADGE[st];
            return <span className={`text-[10px] px-1.5 py-0.5 rounded whitespace-nowrap ${b.cls}`} title={b.title} data-testid={`npd-${st}-${n.npd_number}`}>{b.label}</span>;
          })()}
          <div className="inline-flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => cloneRow(n)}
              className="text-slate-400 hover:text-orange-600 transition"
              title="Clone this costing"
              data-testid={`npd-clone-${n.npd_number}`}
            >
              <Copy size={15} />
            </button>
            <button
              type="button"
              onClick={() => { copyPart("npd", n.npd_id, n.part_name || n.npd_number); toast.success(`Copied "${n.part_name || n.npd_number}". Open any project and click "Paste part".`); }}
              className="text-slate-400 hover:text-indigo-600 transition"
              title="Copy this costing — paste into any project"
              data-testid={`npd-copy-${n.npd_number}`}
            >
              <ClipboardCopy size={15} />
            </button>
            {n.project_id && convertToSingle && (
              <button
                type="button"
                onClick={() => convertToSingle(n)}
                className="text-blue-600 hover:text-blue-800 transition"
                title="Make this a single costing — moves the part out of its project with a new NPD number"
                data-testid={`npd-to-single-${n.npd_number}`}
              >
                <NotepadText size={15} />
              </button>
            )}
            {canDeleteThis && (
              <button
                type="button"
                onClick={() => deleteRow(n)}
                className="text-slate-400 hover:text-red-600 transition"
                title={canDeleteAny ? "Soft-delete" : "Delete (Under Approval)"}
                data-testid={`npd-delete-${n.npd_number}`}
              >
                <Trash2 size={15} />
              </button>
            )}
            <button type="button" onClick={() => onDownloadPdf(n)} disabled={!!pdfBusyId} className="text-slate-500 hover:text-orange-600 p-1 disabled:opacity-40" title="Download quote as PDF" data-testid={`npd-download-${n.npd_number}`}>
              <Download size={16} />
            </button>
            <a href={`/npd/${n.npd_id}/preview`} target="_blank" rel="noreferrer" className="text-sky-600 hover:text-sky-700 p-1" title="View Quote" data-testid={`npd-view-${n.npd_number}`}>
              <Eye size={16} />
            </a>
            <Link to={`/npd/${n.npd_id}`} className="text-orange-600 hover:text-orange-700 p-1 font-medium" title="Edit Quote" data-testid={`npd-edit-${n.npd_number}`}>
              <Pencil size={16} />
            </Link>
          </div>
        </div>
      </td>
    </tr>
  );
}

// Assembly row for the unified "All" view — mirrors CostingRow's columns so assemblies sit
// inline next to single costings and projects. Links out to the assembly screens.
function AssemblyRow({ a, user, units, hideMargin, customerName }) {
  const approvalState = a.approved_for_export
    ? "approved"
    : (String(a.status || "").toLowerCase() && String(a.status).toLowerCase() !== "draft" ? "under_approval" : "draft");
  const compCount = (a.components || []).length;
  return (
    <tr className="hover:bg-slate-50 bg-teal-50/40" data-testid={`asm-row-${a.assembly_number}`}>
      <td className="px-2 py-2.5 font-mono text-xs text-slate-600">
        <div>{a.assembly_number}</div>
        {a.approved_for_export && (
          <div className="flex items-center gap-1 text-emerald-600 text-[10px] mt-0.5">
            <CheckCircle2 size={11} /> Approved
          </div>
        )}
        {a.outcome === "won" && (
          <div className="flex items-center gap-1 text-amber-700 text-[10px] mt-0.5"><Award size={11} /> Won</div>
        )}
        {a.outcome === "lost" && <div className="text-red-700 text-[10px] mt-0.5">Lost</div>}
        {a.outcome === "dropped" && <div className="text-stone-700 text-[10px] mt-0.5">Dropped</div>}
        {(a.misc_files || []).length > 0 && (
          <div className="flex items-center gap-1 text-slate-500 text-[10px] mt-0.5" title={`${a.misc_files.length} attachment${a.misc_files.length > 1 ? "s" : ""}`}>
            <Paperclip size={11} /> {a.misc_files.length}
          </div>
        )}
      </td>
      {user?.role === "admin" && (
        <td className="px-2 py-2.5">
          <span className="text-xs font-mono bg-slate-100 text-slate-700 px-2 py-0.5 rounded">
            {(units.find((un) => un.unit_id === a.unit_id) || {}).unit_code || "—"}
          </span>
        </td>
      )}
      <td className="px-2 py-2.5 text-xs text-slate-500 whitespace-nowrap" title="Created">{fmtDate(a.created_at)}</td>
      <td className="px-2 py-2.5 text-slate-700">{customerName(a.customer_id)}</td>
      <td className="px-2 py-2.5">
        <div className="font-medium text-slate-900 truncate max-w-[160px]">
          {a.assembly_name}
          <span className="ml-1.5 inline-flex items-center gap-0.5 text-[9px] font-bold px-1.5 py-0.5 rounded bg-teal-100 text-teal-700 uppercase tracking-wide align-middle whitespace-nowrap" data-testid={`asm-badge-${a.assembly_number}`}><Layers size={9} /> Assembly</span>
        </div>
        <div className="text-xs text-slate-500">{compCount} sub-part{compCount === 1 ? "" : "s"}</div>
      </td>
      <td className="px-2 py-2.5">
        {a.project_id ? (
          <Link
            to={`/projects/${a.project_id}`}
            className="inline-flex items-center gap-1 text-xs font-mono bg-sky-50 text-sky-700 hover:bg-sky-100 px-2 py-0.5 rounded border border-sky-200 transition"
            title="View this project"
          >
            <Briefcase size={11} />
            {a.assembly_number?.split("/")[0]}
          </Link>
        ) : (
          <span className="text-xs text-slate-400">—</span>
        )}
      </td>
      <td className="px-2 py-2.5">
        <span className={`text-xs font-medium px-2 py-0.5 rounded ${STATUS_BADGE[a.status] || STATUS_BADGE.draft}`}>
          {(a.status || "draft").toUpperCase()}
        </span>
      </td>
      <td className="px-2 py-2.5 text-right font-mono"><span title={formatINR(a.grand_total)}>{formatINRCompact(a.grand_total)}</span></td>
      <td className="px-2 py-2.5 text-right font-mono text-slate-400">—</td>
      {!hideMargin && (
        <td className="p-3 text-right">
          {a.margin_pct === null || a.margin_pct === undefined ? (
            <span className="text-slate-400">—</span>
          ) : (
            <span className={`font-mono ${a.margin_red_flag ? "text-red-600 font-semibold" : "text-slate-700"}`}>
              {a.margin_pct?.toFixed(2)}%
              {a.margin_red_flag && <AlertTriangle size={12} className="inline ml-1" />}
            </span>
          )}
        </td>
      )}
      <td className="px-2 py-2.5 text-right align-top">
        <div className="flex flex-col items-end gap-1.5">
          {(() => {
            const ob = OUTCOME_BADGE[a.outcome];
            if (ob) return <span className={`text-[10px] px-1.5 py-0.5 rounded whitespace-nowrap ${ob.cls}`} title={ob.title}>{ob.label}</span>;
            const b = APPROVAL_BADGE[approvalState];
            return <span className={`text-[10px] px-1.5 py-0.5 rounded whitespace-nowrap ${b.cls}`} title={b.title}>{b.label}</span>;
          })()}
          <div className="inline-flex items-center gap-1.5">
            <a href={`/assemblies/${a.assembly_id}/preview`} target="_blank" rel="noreferrer" className="text-sky-600 hover:text-sky-700 p-1" title="View assembly quote" data-testid={`asm-view-${a.assembly_number}`}>
              <Eye size={16} />
            </a>
            <Link to={`/assemblies/${a.assembly_id}`} className="text-orange-600 hover:text-orange-700 p-1 font-medium" title="Edit assembly" data-testid={`asm-edit-${a.assembly_number}`}>
              <Pencil size={16} />
            </Link>
          </div>
        </div>
      </td>
    </tr>
  );
}

function EmptyState({ groupOnly }) {
  return (
    <div className="text-center py-16">
      <div className="w-14 h-14 mx-auto bg-orange-50 rounded-full flex items-center justify-center text-orange-600 mb-3">
        {groupOnly ? <Briefcase size={22} /> : <FileText size={22} />}
      </div>
      <p className="text-sm font-medium text-slate-700">
        {groupOnly ? "No projects match your filters" : "No costings match your filters"}
      </p>
      <p className="text-xs text-slate-500 mt-1">Try adjusting filters or click "+ New Costing" above to start one.</p>
    </div>
  );
}
